/**
 * Apply a company's branding at boot.
 *
 * The tokens in @cqt/brand are already semantic — `--accent`, `--plate`,
 * `--plate-text` — and every component reads them rather than a raw colour. So
 * branding a company is overriding four custom properties on the root element,
 * and nothing else has to know.
 *
 * ### Why this is applied before the app renders
 *
 * A company's colours arriving one frame after the first paint is a visible
 * flash of the platform default, which is the most obvious kind of bug for
 * someone who only ever sees their own branding. `loadConfig()` already blocks
 * rendering, so this runs in the same gap.
 *
 * ### Why the values are re-checked here
 *
 * `/config.json` already filters them (routers/public.py), and this checks again
 * anyway. Not defence in depth for its own sake: these strings go into
 * `style.setProperty`, and the server that filtered them is a different
 * deployable that could be older than this bundle. A colour is either a plain
 * value or it is dropped.
 */

/** #rgb, #rrggbb, or a bare CSS keyword. Nothing with parentheses or a URL. */
const COLOUR = /^(#[0-9a-fA-F]{3}|#[0-9a-fA-F]{6}|[a-zA-Z]{3,20})$/;

/** Brand key to the CSS custom property it overrides. */
const TOKENS: Record<string, string> = {
  accent: "--accent",
  accentBright: "--accent-hover",
  plate: "--plate",
  plateText: "--plate-text",
};

export interface Brand {
  accent?: string;
  accentBright?: string;
  plate?: string;
  plateText?: string;
  logoUrl?: string;
  wordmark?: string;
  favicon?: string;
}

/**
 * Set the company's colours and favicon on the document.
 *
 * Absent or malformed values are left alone rather than cleared, so a company
 * with partial branding gets the platform default for the rest instead of an
 * unstyled page.
 */
/** WCAG 2.1 relative luminance of an #rgb/#rrggbb colour, or null if unparseable. */
function luminance(colour: string): number | null {
  let hex = colour.trim().replace(/^#/, "");
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;      // a CSS keyword; not ours to judge
  const channel = (v: number) => {
    const n = v / 255;
    return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = [0, 2, 4].map((i) => channel(parseInt(hex.slice(i, i + 2), 16)));
  return 0.2126 * (r as number) + 0.7152 * (g as number) + 0.0722 * (b as number);
}

const contrast = (a: number, b: number) =>
  (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

/**
 * The text colour to put ON the accent — black or white, whichever is readable.
 *
 * It has to be computed, because it depends on a colour the COMPANY chooses.
 * `--accent-ink` used to be a fixed near-black, which was right for the brass
 * this platform was originally built around and wrong for everyone else:
 *
 *     CQT     #b8975a  black 7.09:1   white 2.76:1
 *     Clara   #1f7a4c  black 3.68:1   white 5.32:1
 *     Ingress #0d3b4c  black 1.63:1   white 12.02:1
 *
 * So Ingress's primary buttons, active tabs and avatars had text at 1.63:1 —
 * invisible — and nobody could have fixed it from the branding screen, because
 * there is no brand key for it and there should not be. It is a consequence of
 * the accent, not a separate decision.
 */
const INK_DARK = "#0b0c0e";
const INK_LIGHT = "#ffffff";

export function accentInk(accent: string): string | null {
  const l = luminance(accent);
  if (l === null) return null;
  const onDark = contrast(l, luminance(INK_DARK) as number);
  const onLight = contrast(l, luminance(INK_LIGHT) as number);
  return onDark >= onLight ? INK_DARK : INK_LIGHT;
}

export function applyBrand(brand: Brand | undefined): void {
  if (!brand) return;
  const root = document.documentElement;

  for (const [key, token] of Object.entries(TOKENS)) {
    const value = brand[key as keyof Brand];
    if (typeof value === "string" && COLOUR.test(value.trim())) {
      root.style.setProperty(token, value.trim());
    }
  }

  // Derived from whatever accent just landed, never from a brand key.
  if (typeof brand.accent === "string" && COLOUR.test(brand.accent.trim())) {
    const ink = accentInk(brand.accent.trim());
    if (ink) root.style.setProperty("--accent-ink", ink);
  }

  // `/brand/…` as well as `https://…`, matching what the SERVER already accepts
  // (`_clean_brand` in routers/public.py). The client accepted only https, so a
  // favicon stored the preferred way — served by the platform rather than
  // hotlinked — was silently ignored and every company kept the icon compiled
  // into the SPA. That is why Ingress's browser tab showed CQT's eagle.
  const favicon = brand.favicon?.trim();
  if (favicon && (favicon.startsWith("https://") || favicon.startsWith("/brand/"))) {
    // Replaced rather than appended: browsers pick one, and which one is not
    // worth relying on.
    for (const link of document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')) {
      link.remove();
    }
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = favicon;
    document.head.appendChild(link);
  }
}
